// Reusable "How to use this module" button + drawer.
// Driven by /app/frontend/src/config/moduleGuides.js — same UX as MTM Help.
import { useState } from 'react';
import { BookOpen, X, ArrowRight } from 'lucide-react';
import { getModuleGuide } from '@/config/moduleGuides';

export default function ModuleHelp({ moduleKey, label = 'How to use this module', compact = false }) {
  const [open, setOpen] = useState(false);
  const guide = getModuleGuide(moduleKey);
  if (!guide) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`module-help-btn-${moduleKey}`}
        className="flex items-center gap-1 px-3 py-1.5 text-[11px] rounded font-semibold whitespace-nowrap"
        style={{ background: `${guide.color}15`, color: guide.color, border: `1px solid ${guide.color}40` }}>
        <BookOpen className="w-3 h-3" /> {compact ? 'Help' : label}
      </button>
      {open && <ModuleHelpDrawer guide={guide} onClose={() => setOpen(false)} />}
    </>
  );
}

function ModuleHelpDrawer({ guide, onClose }) {
  const [selectedId, setSelectedId] = useState(guide.tabs[0]?.id);
  const item = guide.tabs.find(t => t.id === selectedId) || guide.tabs[0];

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid={`module-help-drawer-${guide.moduleKey}`}>
      <div className="flex-1" style={{ background: 'rgba(0,0,0,0.6)' }} />
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-3xl h-full overflow-y-auto"
        style={{ background: 'var(--card-bg)', borderLeft: '1px solid var(--border-color)' }}>

        {/* Sticky header */}
        <div className="sticky top-0 px-4 py-3 flex items-center justify-between"
          style={{ background: guide.color, color: '#fff', zIndex: 10 }}>
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">Module Guide</div>
            <div className="text-base font-bold">How to use {guide.title}</div>
            {guide.subtitle && <div className="text-[10px] opacity-80 mt-0.5">{guide.subtitle}</div>}
          </div>
          <button onClick={onClose}
            data-testid={`module-help-close-${guide.moduleKey}`}
            className="p-1 rounded hover:bg-white/20">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-12 gap-0" style={{ minHeight: 'calc(100vh - 64px)' }}>
          {/* Left nav — tabs */}
          <nav className="col-span-4 border-r overflow-y-auto"
            style={{ borderColor: 'var(--border-color)' }}>
            {guide.tabs.map(t => {
              const Icon = t.Icon;
              const active = selectedId === t.id;
              return (
                <button key={t.id} onClick={() => setSelectedId(t.id)}
                  data-testid={`module-help-tab-${guide.moduleKey}-${t.id}`}
                  className="w-full text-left px-3 py-2 text-[11px] flex items-center gap-2 transition"
                  style={{
                    background: active ? `${guide.color}15` : 'transparent',
                    color: active ? guide.color : 'var(--text-secondary)',
                    borderLeft: `3px solid ${active ? guide.color : 'transparent'}`,
                  }}>
                  {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />} {t.label}
                </button>
              );
            })}
          </nav>

          {/* Right pane — content for active tab */}
          <article className="col-span-8 p-4 space-y-3" data-testid={`module-help-content-${guide.moduleKey}`}>
            {item && (
              <>
                <div className="flex items-center gap-2">
                  {item.Icon && <item.Icon className="w-5 h-5" style={{ color: guide.color }} />}
                  <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{item.label}</h2>
                </div>
                <Section title="Purpose" body={item.purpose} />
                <Section title="Source of data"
                  body={<code className="text-[10px] font-mono px-1 py-0.5 rounded inline-block"
                    style={{ background: 'var(--card-bg-alt)' }}>{item.source}</code>} />
                <Section title="When to open it" body={item.when} />
                <div>
                  <h4 className="text-[10px] uppercase tracking-wide font-bold mb-1"
                    style={{ color: 'var(--text-muted)' }}>Step-by-step</h4>
                  <ol className="space-y-1.5 text-[12px] list-none">
                    {(item.flow || []).map((step, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center"
                          style={{ background: guide.color, color: '#fff' }}>{i + 1}</span>
                        <span style={{ color: 'var(--text-secondary)' }}>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                {item.tips && item.tips.length > 0 && (
                  <div>
                    <h4 className="text-[10px] uppercase tracking-wide font-bold mb-1"
                      style={{ color: 'var(--text-muted)' }}>Tips & gotchas</h4>
                    <ul className="space-y-1 text-[12px] list-none">
                      {item.tips.map((tip, i) => (
                        <li key={i} className="flex gap-2">
                          <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: guide.color }} />
                          <span style={{ color: 'var(--text-secondary)' }}>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </article>
        </div>
      </div>
    </div>
  );
}

function Section({ title, body }) {
  return (
    <div>
      <h4 className="text-[10px] uppercase tracking-wide font-bold mb-1" style={{ color: 'var(--text-muted)' }}>{title}</h4>
      <div className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{body}</div>
    </div>
  );
}
